import { useCallback, useEffect, useMemo, useState } from 'react';
import { createContext, useContext, type ReactNode } from 'react';
import { DEFAULT_CODE, findInstrument, type Instrument } from './instruments';
import { clamp } from './layout';
import type { Period } from './app';
import { isoWeekKey, isoWeekMonday, monthKey, scopeOf } from './scopeKey';
import { api } from './api';
import { useAuth } from './auth';

/**
 * 当前选中指数 + 周期 + 复盘日期（需求 §F1 / §F2.1 / §F2.2）
 *
 * 提升到 AppShell 之上，使行情卡片、图表页、新闻面板、笔记编辑器共享同一份状态：
 *  · 选中指数 → 卡片 / 图表头部 / 顶栏快切三处联动
 *  · 周期     → 图表与**笔记 scope** 必须一致（周K 看的是周记，不是日记）
 *  · 复盘日期 → 图表定位 + 新闻日期 + 日记 scope 三者同源
 *
 * 这是最容易出错的一处耦合：三者各存一份就会出现「看着周K 却在写日记」。
 */
interface SelectionState {
  instrument: Instrument;
  /** 选中指数（会切换图表视图） */
  select: (code: string) => void;
  /** 仅选中，不切视图（顶栏快切用） */
  selectOnly: (code: string) => void;

  /** K线周期。MIN1/MIN5 表示分时/5日分时（基于 tick，非日K聚合） */
  period: Period;
  setPeriod: (p: Period) => void;

  /** 当前查看的交易日（YYYY-MM-DD）。null = 跟随最新交易日 */
  date: string | null;
  setDate: (d: string | null) => void;

  /** 最新交易日（由日历接口写入）；date 为 null 时前端用它 */
  latestTradingDay: string | null;
  setLatestTradingDay: (d: string) => void;

  /**
   * 该指数的全部交易日（升序）。
   *
   * ⚠️ `/api/trading-days` 一直返回这整个数组，但改前**只取了 latest**、
   *    把 dates 整个丢掉了 —— 于是「按交易日翻页」无从实现，只能靠复盘页的
   *    月历跳转。日期筛选器的 `◀▶` 依赖它。
   *    A/H 股交易日不同，且港股比 A 股多若干天，故必须按指数分别取。
   */
  tradingDays: string[];
  /** 按当前 scope 步进 n 步（day 走交易日历；week/month/year 按自然周期） */
  stepDate: (n: number) => void;

  /** 有效日期：显式选中优先，否则最新交易日 */
  effectiveDate: string | null;

  /** 由 period + date 推导笔记 scope 与 scope_key（保证三者一致） */
  scope: { scope: 'day' | 'week' | 'month' | 'year'; scopeKey: string };
}

const Ctx = createContext<SelectionState | null>(null);

const STORAGE_CODE = 'selectedCode';
const STORAGE_PERIOD = 'period';
const STORAGE_DATE = 'reviewDate';

function read<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(`stock_note:${key}` as string) as T) ?? fallback;
  } catch {
    return fallback;
  }
}
function write(key: string, v: string): void {
  try {
    localStorage.setItem(`stock_note:${key}`, v);
  } catch {
    /* 隐私模式：偏好丢失不应阻塞使用 */
  }
}

export function SelectionProvider({ children }: { children: ReactNode }) {
  /**
   * 日历请求依赖登录态：未登录时 /api/* 全部 401，
   * 若只在 code 变化时取一次，登录后不会重试 → latestTradingDay 恒 null
   * → 笔记面板一直显示「请先选择一个交易日」。
   * 故把 authKey（登录态指纹）纳入依赖，登录/登出都会重新取。
   */
  const { user, ready: authReady } = useAuth();
  const authKey = authReady ? (user?.id ?? 'anon') : 'pending';
  const [code, setCode] = useState<string>(() => {
    const saved = read(STORAGE_CODE, DEFAULT_CODE);
    return findInstrument(saved) ? saved : DEFAULT_CODE;
  });
  const [period, setPeriodState] = useState<Period>(() => read(STORAGE_PERIOD, 'D') as Period);
  /**
   * ⚠️ 必须把空串归一为 null。
   * `date ?? latestTradingDay` 只在 null/undefined 时回退，空串会原样通过，
   * 导致 effectiveDate 恒为 '' → 笔记面板永远显示「请先选择一个交易日」。
   */
  const [date, setDateState] = useState<string | null>(() => {
    const saved = read(STORAGE_DATE, '');
    return saved === '' ? null : saved;
  });
  const [latestTradingDay, setLatest] = useState<string | null>(null);
  const [tradingDays, setTradingDays] = useState<string[]>([]);

  /**
   * 最新交易日由 provider 自己拉取，**不从图表组件派生**。
   *
   * 之前放在 ChartZone 的 useEffect 里（从 bars 末位取日期），造成死锁：
   *   分时周期 → kline 请求被禁用 → bars 为空 → 不回写 latestTradingDay
   *   → effectiveDate 为 null → 分时请求也无法发出 → 永远拿不到日期。
   * 即「分时需要日期才能取数，而日期要靠取数才知道」。
   * 这里改由独立的日历请求提供，与周期无关。
   */
  useEffect(() => {
    if (authKey === 'pending') return;
    let cancelled = false;
    void api
      .tradingDays(code)
      .then((r: { latest: string | null; dates: string[] }) => {
        if (cancelled) return;
        setTradingDays(r.dates ?? []);
        if (r.latest) setLatest(r.latest);
      })
      .catch(() => {
        // 日历不可用时保留旧值：宁可沿用上一次已知交易日，也不要退化为「无日期」
      });
    return () => { cancelled = true; };
  }, [code, authKey]);

  const select = useCallback((next: string) => {
    if (!findInstrument(next) || next === code) return;
    setCode(next);
    write(STORAGE_CODE, next);
    // 显式选中的日期属于旧指数（A/H 的交易日不同），切换后必须丢弃，
    // 否则新指数可能停在一个它并不存在的交易日上。
    setDateState(null);
    write(STORAGE_DATE, '');
    /**
     * ⚠️ **不要**在这里清空 latestTradingDay。
     *
     * effectiveDate = date ?? latestTradingDay。切换指数时 date 被清空，
     * 若再把 latest 也置 null，effectiveDate 会经历一段 null 窗口。而分时
     * 周期下 klineQ 本就禁用（enabled: !isMinutePeriod），minuteQ 又要求
     * `!!effectiveDate` —— 于是两路数据同时停摆，图表区主图与副图全空，
     * 直到 tradingDays 请求返回才恢复。表现就是「切指数偶尔空白，多切几次
     * 又好了」。
     *
     * latest 是日历事实，下面的 useEffect 会按新 code 重新拉取并覆盖；
     * 保留旧值最多让 effectiveDate 短暂指向旧指数的最新交易日，远比整块
     * 空白可接受（且分时请求拿到不存在的日期会返回 unavailable，有明确
     * 提示，不会伪造数据）。
     */
  }, [code]);

  const setPeriod = useCallback((p: Period) => {
    setPeriodState(p);
    write(STORAGE_PERIOD, p);
  }, []);

  const setDate = useCallback((d: string | null) => {
    // 空串与 null 语义不同（见上面 read 的注释），写入时统一编码为 null
    setDateState(d === '' ? null : d);
    write(STORAGE_DATE, d ?? '');
  }, []);

  const instrument = useMemo(
    () => findInstrument(code) ?? findInstrument(DEFAULT_CODE)!,
    [code],
  );

  /**
   * 按 scope 步进 n 步。
   *
   * day 走**交易日历**：日历里没有的日子（周末/节假日）直接跳过，
   * 否则 `◀` 一下落到周六，笔记面板显示一条永远不会有数据的记录。
   * week/month/year 按自然周期推 —— 它们没有"交易日"的语义，
   * 前端有 isoWeekMonday / monthKey 可算，不必回服务端。
   */
  const stepDate = useCallback(
    (n: number) => {
      if (n === 0) return;
      const cur = date ?? latestTradingDay;
      if (!cur) return;
      if (period === 'MIN1' || period === 'MIN5' || period === 'D') {
        // 分时本质是日内视图，scope 恒为 day
        const pool = tradingDays.length > 0 ? tradingDays : [cur];
        const idx = pool.indexOf(cur);
        if (idx === -1) {
          // 选中的日期不在日历里（如用户手输了休市日）→ 就近吸附
          const next = pool.find((d) => (n > 0 ? d > cur : d < cur));
          if (next) setDate(next);
          return;
        }
        const t = clamp(idx + n, 0, pool.length - 1);
        setDate(pool[t]!);
        return;
      }
      if (period === 'W') {
        const monday = isoWeekMonday(isoWeekKey(cur));
        if (!monday) return;
        const d = new Date(`${monday}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + n * 7);
        setDate(d.toISOString().slice(0, 10));
        return;
      }
      if (period === 'M') {
        const d = new Date(`${monthKey(cur)}-01T00:00:00Z`);
        d.setUTCMonth(d.getUTCMonth() + n);
        setDate(d.toISOString().slice(0, 10));
        return;
      }
      // 年 K
      const y = Number(cur.slice(0, 4)) + n;
      setDate(`${String(y).padStart(4, '0')}-${cur.slice(5)}`);
    },
    [date, latestTradingDay, period, tradingDays, setDate],
  );

  const effectiveDate = date ?? latestTradingDay;

  /**
   * 笔记 scope 随周期走，但分时/5日分时本质是「日内」视图，
   * 故映射回 day —— 否则看分时会去写周记。
   */
  const scope = useMemo(
    () => (effectiveDate
      ? scopeOf(period, effectiveDate)
      : { scope: 'day' as const, scopeKey: '' }),
    [period, effectiveDate],
  );

  const value = useMemo<SelectionState>(
    () => ({
      instrument, select, selectOnly: select,
      period, setPeriod,
      date, setDate,
      latestTradingDay, setLatestTradingDay: (d) => { setLatest(d); },
      tradingDays, stepDate,
      effectiveDate, scope,
    }),
    [instrument, select, period, setPeriod, date, setDate, latestTradingDay,
     tradingDays, stepDate, effectiveDate, scope],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSelection(): SelectionState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSelection 必须在 SelectionProvider 内使用');
  return ctx;
}

/**
 * 仅取指数（避免不需要其他状态的组件订阅过多 context 变化）。
 * 行情卡片、顶栏快切用这个；图表与笔记用完整的 useSelection。
 */
export function useSelectedInstrument(): Pick<SelectionState, 'instrument' | 'select' | 'selectOnly'> {
  const { instrument, select, selectOnly } = useSelection();
  return { instrument, select, selectOnly };
}
